/**
 * /earthworks/change-orders — the change-order register (EW-5, GET
 * /earthworks/change-orders). Filters are the API's: status (chips) and job.
 * The list is unpaginated (the API caps it at 500), so the band's count is a
 * tally of the rows returned. The money column is the approved subtotal once
 * approved, otherwise the lines as they stand; absent → `—`.
 *
 * A change order is raised from its job (the job's Change orders tab).
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilePen } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { useEwJobs } from '../../hooks/useEarthworks';
import { useEwChangeOrders } from '../../hooks/useEwTickets';
import { fmtMoney } from '../../lib/earthworks';
import { EW_CO_STATUSES, canWorkEwTickets, ewCoSkin } from '../../lib/ew-tickets';
import type { EwChangeOrder } from '../../types/earthworks';
import { useOpenRecord } from '../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EwBadge, EwChip, EwChipRow, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';

export const ewCoMoney = (c: Pick<EwChangeOrder, 'status' | 'subtotal' | 'linesTotal' | 'currency'>) =>
  fmtMoney(c.status === 'approved' ? c.subtotal : c.linesTotal, c.currency);

export default function EwChangeOrdersScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canRead = canWorkEwTickets(user?.role);
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const jobId = params.get('job') ?? '';
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };
  const query = useEwChangeOrders({ status: status || undefined, job_id: jobId || undefined }, canRead);
  const jobs = useEwJobs();
  const rows = query.data ?? [];

  const columns: EwColumn<EwChangeOrder>[] = [
    { key: 'number', header: 'Change order', render: (c) => <span className="font-mono font-bold text-white">{c.coNumber}</span> },
    { key: 'job', header: 'Job', render: (c) => (<><span className="font-mono">{c.jobNumber || '—'}</span><p className="text-xs text-fieldiq-muted">{c.siteName || '—'}</p></>) },
    { key: 'customer', header: 'Customer', render: (c) => c.customerName || '—' },
    { key: 'reason', header: 'Reason', render: (c) => <span className="line-clamp-2">{c.reason || '—'}</span> },
    { key: 'status', header: 'Status', render: (c) => <EwBadge skin={ewCoSkin(c.status)} value={c.status} /> },
    { key: 'total', header: 'Amount', align: 'right', render: (c) => <span className="font-mono">{ewCoMoney(c)}</span> },
  ];

  return (
    <div data-testid="ew-change-orders-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Change Orders"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <FilePen className="size-4" aria-hidden="true" />, label: 'Shown', tone: 'neutral', loading: query.isLoading, value: query.isLoading ? undefined : String(rows.length) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canRead ? (
            <EwPanel eyebrow="Change orders" title="Change orders">
              <EwEmpty>Change orders carry prices, so the register is for the office, supervisors and dispatch. Your job's change orders are on its field screen.</EwEmpty>
            </EwPanel>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <EwChipRow label="Status">
                  <EwChip label="All" active={!status} onClick={() => set('status', '')} />
                  {EW_CO_STATUSES.map((s) => (
                    <EwChip key={s.value} label={s.label} active={status === s.value} onClick={() => set('status', s.value)} />
                  ))}
                </EwChipRow>
                <select className={`${EW_INPUT} w-auto`} aria-label="Job" value={jobId} onChange={(e) => set('job', e.target.value)}>
                  <option value="">All jobs</option>
                  {(jobs.data?.data ?? []).map((j) => (
                    <option key={j.id} value={j.id}>{j.jobNumber}{j.siteName ? ` · ${j.siteName}` : ''}</option>
                  ))}
                </select>
              </div>
              <EwPanel eyebrow="Change orders" title="Change orders" testId="ew-cos-register" footer={!query.isLoading && rows.length ? `${rows.length} change order${rows.length === 1 ? '' : 's'}` : undefined}>
                <EwRegister
                  query={query}
                  rows={rows}
                  columns={columns}
                  rail={(c) => ewCoSkin(c.status).rail}
                  onOpen={(c) => openRecord(`/earthworks/change-orders/${c.id}`)}
                  subject="change orders"
                  testId="ew-co-row"
                  minWidth="min-w-[900px]"
                  empty={<EwEmpty>No change orders{status || jobId ? ' match these filters' : ' yet'}. Raise one from a job's Change orders tab.</EwEmpty>}
                  card={(c) => (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-bold text-white">{c.coNumber}</p>
                          <p className="mt-0.5 truncate text-xs text-fieldiq-muted">{c.jobNumber} · {c.reason}</p>
                        </div>
                        <EwBadge skin={ewCoSkin(c.status)} value={c.status} />
                      </div>
                      <p className="mt-2 font-mono text-sm text-white">{ewCoMoney(c)}</p>
                    </>
                  )}
                />
              </EwPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
